import { isEmpty } from '@fruitful/fruitils';
import { router, useLocalSearchParams } from 'expo-router';
import React, { useState } from 'react';
import { type SceneRendererProps, TabView } from 'react-native-tab-view';
import {
    FullScreenSpinner,
    ScreenContainer,
    useToastController,
    View,
} from 'smoothie';

import { useAuthedUser } from '../../../common/providers/AuthedUserProvider';
import { preferLegalName } from '../../../common/utils/bankingUtils';
import {
    toCardFriendlyName,
    truncateCardFriendlyName,
} from '../../../common/utils/cardUtils';
import LogRocketFacade from '../../../logger/log-rocket/LogRocketFacade';
import { captureException } from '../../../logger/Logger';
import useIssueCard from '../../hooks/card/useIssueCard';
import useGetAccountDisplayDetailsFromGlobalState from '../../hooks/useGetAccountDisplayDetailsFromGlobalState';
import useGetAllBankAccountDetails from '../../hooks/useGetAllBankAccountDetails';
import { CardForm } from '../../types/Card';
import { OrderCardDetails } from './steps/OrderCardDetails';
import { PhysicalCardSent } from './steps/PhysicalCardSent';

enum RequestPhysicalCardStep {
    PHYSICAL_CARD_DETAILS = 'physicalCardDetails',
    CONFIRM_PHYSICAL_CARD_DETAILS = 'confirmPhysicalCardDetails',
    PHYSICAL_CARD_SENT = 'physicalCardSent',
}

const RequestPhysicalCard = () => {
    const { syncteraAccountId } = useLocalSearchParams<{
        syncteraAccountId: string;
    }>();

    LogRocketFacade.tagPage('RequestPhysicalCard');

    const toast = useToastController();

    const user = useAuthedUser();
    const { account } = useGetAccountDisplayDetailsFromGlobalState({
        syncteraAccountId,
    });

    const cardRequester =
        [account?.primaryUser, account?.jointUser].find(
            (acctUser) => acctUser?.id === user.id,
        ) ?? user;
    const [firstName, lastName] = preferLegalName(cardRequester);

    const { chargeSecuredSyncteraAccountId } = account ?? {};

    const cardFriendlyFirstName = toCardFriendlyName(firstName);
    const cardFriendlyLastName = toCardFriendlyName(lastName);

    const [index, setIndex] = useState(0);
    const routes = Object.values(RequestPhysicalCardStep).map((step) => ({
        key: step,
    }));

    const getRouteIndex = (step: RequestPhysicalCardStep) =>
        routes.findIndex(({ key }) => key === step);

    const confirmPhysicalCardIndex = getRouteIndex(
        RequestPhysicalCardStep.CONFIRM_PHYSICAL_CARD_DETAILS,
    );
    const physicalCardSent = getRouteIndex(
        RequestPhysicalCardStep.PHYSICAL_CARD_SENT,
    );

    const { mutateAsync: issueCard, isPending: isIssueCardPending } =
        useIssueCard();
    const { refetchAccountDetails } = useGetAllBankAccountDetails(false);

    const onSubmit = async () => {
        const { phoneNumber, userAddress } = user;
        const { line1, line2, city, postalCode, state } = userAddress ?? {};

        const requiredFields = [
            chargeSecuredSyncteraAccountId,
            cardFriendlyFirstName,
            cardFriendlyLastName,
            phoneNumber,
            line1,
            city,
            postalCode,
            state,
        ];
        const allFieldsPresent = requiredFields.every(
            (field) => !isEmpty(field),
        );

        if (!allFieldsPresent) {
            captureException(
                new Error(
                    'Error issuing physical card. Missing mailing address info.',
                ),
            );
            router.navigate('/home/banking');
            toast.show('Unable to issue your physical card.');
            return;
        }

        try {
            await issueCard({
                body: {
                    form: CardForm.PHYSICAL,
                    syncteraAccountId: chargeSecuredSyncteraAccountId as string,
                    embossName: truncateCardFriendlyName(
                        cardFriendlyFirstName,
                        cardFriendlyLastName,
                    ),
                    shipping: {
                        recipientFirstName: cardFriendlyFirstName,
                        recipientLastName: cardFriendlyLastName,
                        phoneNumber: phoneNumber as string,
                        address: {
                            line1: line1 as string,
                            ...(line2 != null && !isEmpty(line2) && { line2 }),
                            city: city as string,
                            postalCode: postalCode as string,
                            state: state as string,
                            countryCode: 'US',
                        },
                    },
                },
            });
            setIndex(physicalCardSent);
        } catch (error) {
            if (error instanceof Error) {
                if (error.message.includes('reached limit for physical card')) {
                    toast.show(
                        'You have reached your limit for physical cards',
                    );
                } else {
                    captureException(error);
                    toast.show(
                        'An error occurred while issuing your physical card. Please try again later.',
                    );
                }
            } else {
                captureException(
                    new Error(
                        'Unknown error occurred while issuing physical card',
                    ),
                );
                toast.show(
                    'An unexpected error occurred. Please try again later.',
                );
            }
        } finally {
            void refetchAccountDetails();
        }
    };

    const onPressBack = () => {
        if (index === confirmPhysicalCardIndex) {
            setIndex(
                getRouteIndex(RequestPhysicalCardStep.PHYSICAL_CARD_DETAILS),
            );
        } else {
            if (router.canGoBack()) {
                router.back();
            } else {
                router.navigate('/home/banking');
            }
        }
    };

    const renderScene = ({
        route,
    }: SceneRendererProps & { route: { key: RequestPhysicalCardStep } }) => {
        if (routes[index]?.key !== route.key) {
            return null;
        }

        const getStepContent = () => {
            switch (route.key) {
                case RequestPhysicalCardStep.PHYSICAL_CARD_DETAILS:
                    return (
                        <OrderCardDetails
                            reviewMode={false}
                            user={user}
                            cardTypes={[CardForm.PHYSICAL]}
                            embossName={truncateCardFriendlyName(
                                cardFriendlyFirstName,
                                cardFriendlyLastName,
                            )}
                            onPress={() => {
                                setIndex(confirmPhysicalCardIndex);
                            }}
                        />
                    );
                case RequestPhysicalCardStep.CONFIRM_PHYSICAL_CARD_DETAILS:
                    return (
                        <OrderCardDetails
                            reviewMode
                            cardTypes={[CardForm.PHYSICAL]}
                            embossName={truncateCardFriendlyName(
                                cardFriendlyFirstName,
                                cardFriendlyLastName,
                            )}
                            user={user}
                            onPress={onSubmit}
                        />
                    );
                case RequestPhysicalCardStep.PHYSICAL_CARD_SENT:
                    return (
                        <PhysicalCardSent
                            embossName={truncateCardFriendlyName(
                                cardFriendlyFirstName,
                                cardFriendlyLastName,
                            )}
                            onPress={() => {
                                router.navigate({
                                    pathname: '/banking/card/manage-card',
                                    params: { syncteraAccountId },
                                });
                            }}
                        />
                    );
                default:
                    return <></>;
            }
        };

        return (
            <ScreenContainer
                hasBackButton={index !== physicalCardSent}
                onBack={onPressBack}
                hasCloseButton={index === physicalCardSent}
                onClose={() => {
                    router.navigate({
                        pathname: '/banking/card/manage-card',
                        params: { syncteraAccountId },
                    });
                }}
                onFallbackRoute="/home/banking"
            >
                {getStepContent()}
            </ScreenContainer>
        );
    };

    if (isIssueCardPending) {
        return <FullScreenSpinner />;
    }

    return (
        <View height="100%">
            <TabView
                swipeEnabled={false}
                navigationState={{ index, routes }}
                renderScene={renderScene}
                renderTabBar={() => <></>}
                onIndexChange={setIndex}
            />
        </View>
    );
};

export default RequestPhysicalCard;
